import { InputStepper } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import { isNumber } from "lodash-es";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import type { RefObject } from "react";
import type { Product, ProductTileCallbacks } from "../../../common/types/productTypes";
import type { GAListInformation } from "../../../common/types/trackingTypes";
import { formatPrice, formatUnit } from "../../../common/utils";
import authStore from "../../../stores/auth/authStore";
import productStore from "../../../stores/product/productStore";
import { isSeasonalProduct } from "../../../stores/product/productUtils";
import theme from "../../../themes/theme";
import AddToCartButton from "../../AddToCartButton";
import DisplayPrice from "../../DisplayPrice";
import PricePerItem from "../../PricePerItem";
import type { InternalProductTileStore, ProductTileSize } from "./ProductTile";

type ProductTileLoggedInProps = {
   product: Product;
   position: number;
   tileState: InternalProductTileStore;
   currentContainerSize: ProductTileSize | undefined;
   callbacks?: ProductTileCallbacks;
   imageRef: RefObject<HTMLImageElement | null>;
   listInformation: GAListInformation;
};

const ProductTileLoggedIn = ({
   product,
   position,
   tileState,
   currentContainerSize,
   callbacks = {},
   imageRef,
   listInformation
}: ProductTileLoggedInProps) => {
   if (!authStore.isLoggedIn()) {
      return null;
   }

   const seasonalProduct = isSeasonalProduct(product);

   if (product.expired) {
      return (
         <p className="text-xs">
            {seasonalProduct ? "Denne varen er utgått for sesongen" : "Denne varen er utgått fra sortimentet"}
         </p>
      );
   }

   if (!productStore.inAssortment(product.sku)) {
      return <p className="text-xs">Denne varen er utenfor sortimentet</p>;
   }

   const stepperAndButtonContainer = cn("flex w-full items-center justify-stretch gap-1", {
      "flex-col gap-2": currentContainerSize === "thin"
   });

   const inputStepper = cn(
      "flex-grow self-center [&>button]:h-[43px] [&>button]:w-[43px] disabled:[&>button]:bg-transparent [&>div]:h-[43px] [&>div]:min-w-[30px] [&>div]:max-w-[120px] [&>div]:flex-grow [&>div]:overflow-hidden",
      { "w-full [&>button]:flex-grow-0 [&>div]:max-w-[120px] [&>div]:flex-grow": currentContainerSize === "thin" }
   );

   const handleQuantityChange = action((qty: number) => {
      tileState.qty = qty;
      return qty;
   });

   return (
      <>
         <DisplayPrice
            className="mb-1"
            price={product.priceBeforeDiscount ?? product.price ?? 0}
            newPrice={product.price ?? 0}
            postPriceDetail={formatUnit(product.unit)}
         />
         <div className="flex flex-col gap-1 mb-2">
            <PricePerItem className="text-xxs text-base-600" product={product} />
            {isNumber(product.comparisonPrice) && theme.showComparisonPrice && !!product.comparisonUnit && (
               <div className="mb-1 text-xxs text-base-600">
                  {formatPrice(product.comparisonPrice)} per {product.comparisonUnit?.toLowerCase()}
               </div>
            )}
         </div>

         <div className={stepperAndButtonContainer}>
            <InputStepper className={inputStepper} value={tileState.qty} onChange={handleQuantityChange} min={1} />

            <AddToCartButton
               product={product}
               qty={tileState.qty}
               imageRefFn={() => imageRef}
               callbacks={callbacks}
               listInformation={listInformation}
               position={position}
               size={currentContainerSize === "thin" ? "regular" : "iconOnly"}
               className={currentContainerSize === "thin" ? "w-full" : ""}
            />
         </div>
      </>
   );
};

export default observer(ProductTileLoggedIn);
